import { useMutation } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import {
	MousePointer2,
	Move,
	Upload,
	Home,
	HousePlus,
	RotateCcw,
	Eraser,
	EllipsisVertical,
	Box,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import type { AssetHomeView } from '@/common/asset';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCesium } from '@/context/CesiumContext';
import { captureHomeView } from '@/lib/utils/homeView';
import { apiErrorMessage } from '@/queries/common';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { updateAsset } from '@/queries/assets/update-asset';
import { queryClient } from '@/queries/query.client';
import { cn } from '@/lib/utils';
import { ROLE } from '../../common/role';
import { Restricted } from '../Restricted';
import DamageDrawTool from './DamageDrawTool';
import DamagePointTool from './DamagePointTool';
import MeasurementDrawer from '@/helper/MeasurementDrawer';

const MeasurePolylineIcon = () => (
	<svg
		viewBox="0 0 30 30"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		height="15px"
		width="15px"
	>
		<g transform="translate(0,-267)">
			<circle cx="3.8532958" cy="293.99896" r="2.0788691" />
			<circle cx="7.2651663" cy="276.26389" r="2.0788691" />
			<circle cx="24.571842" cy="285.56577" r="2.0788691" />
			<circle cx="26.916754" cy="270.38345" r="2.0788691" />
			<path d="m 3.7523356,294.14823 3.602242,-17.81109 17.3608064,9.35582 2.401494,-15.00934" />
		</g>
	</svg>
);

const MeasureHeightIcon = () => (
	<svg
		viewBox="0 0 30 30"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		height="15px"
		width="15px"
	>
		<g transform="translate(0,-267)">
			<path d="m 15.042838,272.34414 0.01712,19.60575" />
			<circle r="2.0788691" cy="270.01154" cx="15.078616" />
			<path d="m 0.64901081,296.20687 8.80039389,-6.01044 7.9375003,3.1183 12.347278,-3.34365" />
		</g>
	</svg>
);

const MeasurePolygonIcon = () => (
	<svg
		viewBox="0 0 30 30"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		height="15px"
		width="15px"
	>
		<g transform="translate(0,-267)">
			<circle r="2.0788691" cy="293.99896" cx="3.8532958" />
			<circle r="2.0788691" cy="282.76791" cx="26.927404" />
			<circle r="2.0788691" cy="270.20621" cx="4.0090437" />
			<path
				d="m 26.326048,283.77014 -9.394396,5.02295 -9.3943948,5.02295 0.3471933,-10.64726 0.3471933,-10.64726 9.0472022,5.62431 z"
				transform="matrix(1.1625734,0,0,0.99297729,-4.6787891,1.2180486)"
			/>
		</g>
	</svg>
);


const IconButton = ({ icon: Icon, active = false, onClick, title, disabled = false }: any) => (
	<button
		onClick={onClick}
		title={title}
		disabled={disabled}
		className={cn(
			'flex items-center justify-center w-8 h-8 rounded-md transition-colors duration-200',
			active
				? 'text-accent-foreground bg-accent'
				: 'text-foreground/70 hover:text-foreground hover:bg-accent',
			disabled && 'opacity-50 cursor-not-allowed'
		)}
	>
		<Icon className="w-4 h-4" />
	</button>
);

const ActionButton = ({ icon: Icon, label, onClick, active }: any) => (
	<button
		onClick={onClick}
		title={label}
		className={cn(
			'flex items-center gap-1.5 px-2 sm:px-3 h-8 rounded-md text-xs font-medium transition-colors duration-200 whitespace-nowrap',
			active
				? 'bg-primary text-primary-foreground shadow-sm hover:opacity-90'
				: 'text-foreground/80 hover:bg-muted/80 hover:text-foreground'
		)}
	>
		<Icon className="w-3.5 h-3.5 shrink-0" />
		<span className="hidden sm:inline">{label}</span>
	</button>
);

const VerticalSeparator = () => <div className="h-5 w-px bg-foreground/40 mx-1 shrink-0" />;

interface MapToolbarProps {
	onDamageDrawComplete?: (positions: any[], type: 'damage' | 'inspection') => void;
	isUploadMode: boolean;
	onToggleUploadMode: () => void;
	isClippingMode: boolean;
	onToggleClippingMode: () => void;
	canUseLimitBox: boolean;
}

export default function MapToolbar({
	onDamageDrawComplete,
	isUploadMode,
	onToggleUploadMode,
	isClippingMode,
	onToggleClippingMode,
	canUseLimitBox,
}: MapToolbarProps) {
	const { t } = useTranslation();
	const { viewer, homeView, setActiveTool } = useCesium();
	const { assetId } = useParams<{ assetId: string }>();
	const { data: asset } = useQuery(getAssetOptions(assetId || ''));

	// "Set current view as home" / "Reset to automatic": one saved pose per asset,
	// stored on the asset record. InitialZoomController picks the change up from
	// the refetched asset and updates the Home button.
	const saveHomeView = useMutation({
		mutationFn: (nextHomeView: AssetHomeView | null) => updateAsset(assetId as string, { homeView: nextHomeView }),
		onSuccess: async (_data, nextHomeView) => {
			toast.success(nextHomeView ? t('viewer.toolbar.homeViewSaved') : t('viewer.toolbar.homeViewReset'));
			await queryClient.invalidateQueries({ queryKey: ['assets', assetId] });
		},
		onError: (error) => toast.error(apiErrorMessage(error)),
	});

	const handleSetHomeView = () => {
		if (!viewer || viewer.isDestroyed() || !assetId) return;
		saveHomeView.mutate(captureHomeView(viewer));
	};

	const handleResetHomeView = () => {
		if (!assetId) return;
		saveHomeView.mutate(null);
	};

	const [isToolboxOpen, setIsToolboxOpen] = useState(false);
	const [measureMode, setMeasureMode] = useState<'polyline' | 'polygon' | 'height' | null>(null);

	const drawerRef = useRef<MeasurementDrawer | null>(null);

	useEffect(() => {
		if (viewer && !drawerRef.current) {
			drawerRef.current = new MeasurementDrawer(viewer);
		}
		return () => {
			if (drawerRef.current) {
				drawerRef.current = null;
			}
		};
	}, [viewer]);

	// Single owner of the shared tool slot: measuring wins while it runs, then whichever
	// of the two CesiumViewer-owned modes is still on, else none.
	useEffect(() => {
		setActiveTool(
			measureMode ? 'measure' : isClippingMode ? 'clipping' : isUploadMode ? 'upload' : 'none'
		);
	}, [measureMode, isClippingMode, isUploadMode, setActiveTool]);

	const handleHomeClick = () => {
		if (!viewer) return;

		if (homeView) {
			if (homeView.type === 'fly') {
				viewer.camera.flyTo({
					...homeView.options,
					duration: 1.5, // smooth flight on click
				});
			} else if (homeView.type === 'zoom') {
				viewer.zoomTo(homeView.target, homeView.offset).catch(console.warn);
			}
		} else {
			// Default global fallback if no specific home was set
			viewer.camera.flyHome(1.5);
		}
	};

	const toggleToolbox = () => {
		setIsToolboxOpen(!isToolboxOpen);
		if (isToolboxOpen) {
			handleClearMeasurements();
			setMeasureMode(null);
			drawerRef.current?.stop();
		}
	};

	const handleMeasureMode = (mode: 'polyline' | 'polygon' | 'height') => {
		if (measureMode === mode) {
			setMeasureMode(null);
			drawerRef.current?.stop();
		} else {
			setMeasureMode(mode);
			drawerRef.current?.start(mode);
		}
	};

	const handleClearMeasurements = () => {
		drawerRef.current?.clear();
		setMeasureMode(null);
	};

	return (
		<div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none flex flex-col items-center gap-2">
			{/* MAIN TOOLBAR */}
			<div className="flex items-center gap-1 bg-background text-foreground p-1 rounded-lg shadow-md border border-border pointer-events-auto">
				<div className="flex items-center gap-0.5 px-0.5">
					<IconButton icon={Home} onClick={handleHomeClick} title={t('viewer.toolbar.home')} />
					<Restricted to={ROLE.ASSETS_UPDATE}>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									title={t('viewer.toolbar.homeViewOptions')}
									aria-label={t('viewer.toolbar.homeViewOptions')}
									disabled={saveHomeView.isPending}
									className="flex items-center justify-center w-8 h-8 rounded-md transition-colors duration-200 text-foreground/70 hover:text-foreground hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-accent-foreground disabled:opacity-50"
								>
									<HousePlus className="w-4 h-4" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start">
								<DropdownMenuItem onClick={handleSetHomeView} disabled={saveHomeView.isPending}>
									<HousePlus /> {t('viewer.toolbar.setHome')}
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={handleResetHomeView}
									disabled={!asset?.homeView || saveHomeView.isPending}
									title={asset?.homeView ? undefined : t('viewer.toolbar.noSavedHome')}
								>
									<RotateCcw /> {t('viewer.toolbar.resetHome')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</Restricted>
					<IconButton
						icon={MousePointer2}
						// active={!measureMode && !isClippingMode && !isUploadMode}
						title={t('common.actions.select')}
					/>
					<IconButton icon={Move} title={t('viewer.toolbar.pan')} />
				</div>

				<VerticalSeparator />

				<div className="flex items-center gap-1 px-1">
					<Restricted to={ROLE.DAMAGES_CREATE}>
						{/* These components control their own button styling! */}
						<DamageDrawTool onDrawComplete={onDamageDrawComplete} />
						<DamagePointTool onDrawComplete={onDamageDrawComplete} />
					</Restricted>
					<Restricted to={ROLE.DATA_UPLOADS_CREATE}>
						<ActionButton
							icon={Upload}
							label={t('common.actions.upload')}
							active={isUploadMode}
							onClick={onToggleUploadMode}
						/>
					</Restricted>
				</div>

				<div className="flex items-center gap-0.5 px-0.5 transition-all">
					<IconButton
						icon={EllipsisVertical}
						title={t('viewer.toolbar.toolbox')}
						active={isToolboxOpen || measureMode !== null || isClippingMode}
						onClick={toggleToolbox}
					/>
				</div>
			</div>

			{/* SECONDARY TOOLBAR */}
			{isToolboxOpen && (
				<div className="flex items-center gap-0.5 bg-background text-foreground p-1 rounded-lg shadow-md border border-border pointer-events-auto animate-in slide-in-from-top-2 fade-in duration-200">
					<IconButton
						icon={MeasurePolylineIcon}
						title={t('viewer.toolbar.measureLine')}
						active={measureMode === 'polyline'}
						onClick={() => handleMeasureMode('polyline')}
					/>
					<IconButton
						icon={MeasurePolygonIcon}
						title={t('viewer.toolbar.measureArea')}
						active={measureMode === 'polygon'}
						onClick={() => handleMeasureMode('polygon')}
					/>
					<IconButton
						icon={MeasureHeightIcon}
						title={t('viewer.toolbar.measureHeight')}
						active={measureMode === 'height'}
						onClick={() => handleMeasureMode('height')}
					/>

					<IconButton
						icon={Eraser}
						title={t('viewer.toolbar.clearMeasurements')}
						onClick={handleClearMeasurements}
					/>
					<VerticalSeparator />

					<IconButton
						icon={Box}
						title={
							canUseLimitBox || isClippingMode
								? t('viewer.toolbar.limitBox')
								: t('viewer.toolbar.limitBoxUnavailable')
						}
						active={isClippingMode}
						// Always enabled while open, so the tool can be closed from anywhere.
						disabled={!canUseLimitBox && !isClippingMode}
						onClick={onToggleClippingMode}
					/>
				</div>
			)}
		</div>
	);
}
